import React from "react";
import '../../styles/common/router-error-message.scss';
import {FontIcon} from "./FontIcon";
import Button from "./Button";
import {logger} from "../../services/log/logger";
import {isRouteErrorResponse, useNavigate, useRouteError} from "react-router-dom";
import * as Sentry from "@sentry/react";

/**
 * Only used by App.tsx
 * Responsible for displaying an error message when a route fails to load. This component differs from the ErrorMessage component in that it is specifically for routing errors.
 * React controls whether this component is displayed which makes it unsuitable for displaying errors that occur outside of routing.
 * We may want to consider creating a third component that both RouterErrorMessage and ErrorMessage can use to display errors.
 */
export default function RouterErrorMessage() {
  const navigate = useNavigate();

  const error = useRouteError();

  const formattedTimestamp = new Date().toLocaleString(undefined, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });

  let errorMessage: string;

  if (isRouteErrorResponse(error)) {
    // error is type `ErrorResponse`
    errorMessage = error.data?.message || error.statusText;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    Sentry.captureException(error as Error);
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else {
    console.error(error);
    errorMessage = 'Unknown error';
  }

  logger.warn({
    message: `ERRORMESSAGE`,
    additionalNote: errorMessage,
  });

  return (
    <div className="error-message">
      <div className="error-modal">
        <FontIcon icon={"icon-exclamation-triangle"}/>
        <div className="message">
          <div className="title">{errorMessage}</div>
          <div>
            <p className="message-text">A routing error has occurred.</p>
          </div>
          <div className="timestamp">{formattedTimestamp}</div>
          <Button text={`Dashboard`} buttonType={"primary"} onClick={() => navigate('/dashboard')} />
          <Button text={`Reload`} buttonType={"secondary"} onClick={() => navigate(0)} />
        </div>
      </div>
    </div>
  );
}
